import { useEffect, useState, useMemo } from "react";
import { usePurchaseVoucher } from "../context/PurchaseVoucherContext";
import { Plus, Pencil, Trash2, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ENTRIES_OPTIONS = [5, 10, 20];
const fmt = (n) => `₹ ${Number(n).toLocaleString("en-IN")}`;

export default function PurchaseVouchers() {
  const navigate = useNavigate();
  const { purchaseVouchers, fetchPurchaseVouchers, loading } = usePurchaseVoucher();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [page, setPage] = useState(1);
  const [entries, setEntries] = useState(5);

  useEffect(() => { fetchPurchaseVouchers(); }, []);
  useEffect(() => { setPage(1); }, [search, sortBy, entries]);

  const data = useMemo(
    () => (Array.isArray(purchaseVouchers) ? purchaseVouchers : []),
    [purchaseVouchers]
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let result = data.filter((item) =>
      item.customername?.toLowerCase().includes(q) ||
      item.note?.toLowerCase().includes(q)
    );
    if (sortBy === "amount_desc") result = [...result].sort((a, b) => Number(b.amount) - Number(a.amount));
    if (sortBy === "amount_asc")  result = [...result].sort((a, b) => Number(a.amount) - Number(b.amount));
    if (sortBy === "name_asc")    result = [...result].sort((a, b) => a.customername?.localeCompare(b.customername));
    return result;
  }, [data, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / entries));
  const paginated  = filtered.slice((page - 1) * entries, page * entries);

  const withBalance = data.filter(i => Number(i.balance) > 0).length;
  const cleared     = data.filter(i => Number(i.balance) === 0).length;

  return (
    <main className="min-h-screen bg-[#fdf6ec] p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center shadow">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 tracking-tight">Purchase Vouchers</h1>
              <p className="text-sm text-gray-500 mt-0.5">Manage your purchase entries</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/create-purchase")}
            className="flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl hover:bg-teal-700 active:scale-95 transition-all font-semibold text-sm shadow-sm w-full sm:w-auto"
          >
            <Plus size={16} /> Add Voucher
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {[
            { label: "Total",        value: data.length,  color: "text-gray-800"  },
            { label: "With Balance", value: withBalance,  color: "text-green-600" },
            { label: "Cleared",      value: cleared,      color: "text-red-500"   },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-2xl px-4 sm:px-6 py-4 sm:py-5 shadow-sm">
              <p className="text-xs sm:text-sm text-gray-400 font-medium">{label}</p>
              <p className={`text-2xl sm:text-3xl font-bold mt-1 ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Search & Sort */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Search voucher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 h-11 px-4 rounded-xl border border-gray-200 bg-white text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 placeholder-gray-400"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-11 px-4 rounded-xl border border-gray-200 bg-white text-sm text-gray-500 outline-none cursor-pointer focus:border-teal-400 sm:min-w-[160px]"
          >
            <option value="">Sort By</option>
            <option value="amount_desc">Amount: High → Low</option>
            <option value="amount_asc">Amount: Low → High</option>
            <option value="name_asc">Name: A → Z</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {["ID", "Supplier", "Amount", "Paid", "Balance", "Note", "Actions"].map((col) => (
                    <th key={col}
                      className={`px-6 py-4 text-left text-xs font-semibold bg-[#f9fafb] uppercase tracking-wide
                        ${["Amount","Paid","Balance"].includes(col) ? "text-right" : ""}
                        ${col === "Actions" ? "text-center" : ""}
                      `}
                    >{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-14 text-gray-400">Loading…</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-14 text-gray-400">No records found</td></tr>
                ) : paginated.map((item, idx) => (
                  <tr key={item.id} className="border-b border-gray-50 hover:bg-[#fdf9f4] transition-colors">
                    <td className="px-6 py-4 text-gray-400 text-xs font-mono">{(page - 1) * entries + idx + 1}</td>
                    <td className="px-6 py-4 font-semibold text-gray-800">{item.customername}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-700">{fmt(item.amount)}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-700">{fmt(item.paid)}</td>
                    <td className="px-6 py-4 text-right font-semibold text-red-500">{fmt(item.balance)}</td>
                    <td className="px-6 py-4 text-gray-400 max-w-[180px] truncate">{item.note || "—"}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-3">
                        <button onClick={() => navigate(`/edit-purchase/${item.id}`)} className="text-teal-400 hover:text-teal-600 transition-colors" title="Edit"><Pencil size={15} /></button>
                        <button className="text-red-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-50">
            {loading ? (
              <div className="text-center py-14 text-gray-400 text-sm">Loading…</div>
            ) : paginated.length === 0 ? (
              <div className="text-center py-14 text-gray-400 text-sm">No records found</div>
            ) : paginated.map((item, idx) => (
              <div key={item.id} className="p-4 hover:bg-[#fdf9f4] transition-colors">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{item.customername}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">#{(page - 1) * entries + idx + 1}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => navigate(`/edit-purchase/${item.id}`)} className="text-teal-400 hover:text-teal-600 p-1.5"><Pencil size={13} /></button>
                    <button className="text-red-400 hover:text-red-600 p-1.5"><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-gray-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-[10px] text-gray-400">Amount</p>
                    <p className="text-xs font-bold text-gray-700">{fmt(item.amount)}</p>
                  </div>
                  <div className="bg-green-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-[10px] text-gray-400">Paid</p>
                    <p className="text-xs font-bold text-green-600">{fmt(item.paid)}</p>
                  </div>
                  <div className="bg-red-50 rounded-xl px-2 py-2 text-center">
                    <p className="text-[10px] text-gray-400">Balance</p>
                    <p className="text-xs font-bold text-red-500">{fmt(item.balance)}</p>
                  </div>
                </div>
                {item.note && <p className="text-xs text-gray-400 mt-2 truncate">Note: {item.note}</p>}
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-[#fdfaf6]">
            <p className="text-xs text-gray-400">
              Showing {filtered.length ? (page - 1) * entries + 1 : 0}–{Math.min(page * entries, filtered.length)} of {filtered.length} entries
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => p - 1)} disabled={page === 1}
                className="px-4 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Prev</button>
              <span className="text-xs font-semibold text-gray-600 px-1">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => p + 1)} disabled={page === totalPages}
                className="px-4 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next</button>
            </div>
          </div>
        </div>

      </div>
    </main>
  );
}